import type { LoadVariableRequest, WorkerResponse } from './texture-worker';
import { DataSource, VariableDataFrame } from './types';

interface PendingRequest {
  resolve: (value: VariableDataFrame) => void;
  reject: (reason: any) => void;
}

interface CachedTileMetadata {
  url: string;
  scale: { min: number; max: number };
  bounds: {
    lat_min: number;
    lat_max: number;
    lon_min: number;
    lon_max: number;
  };
}

export class TextureLoadingService {
  private worker: Worker | null = null;
  private pendingRequests = new Map<string, PendingRequest>();
  private requestIdCounter = 0;
  private isWorkerReady = false;
  private readyPromise: Promise<void> | null = null;

  // Cache for presigned URLs - key format: "model_forecast_leadTime_variable"
  private urlCache = new Map<string, CachedTileMetadata>();

  private initializeWorker(): Promise<Worker> {
    if (!this.worker) {
      // Create worker from the wrapper entry to ensure polyfills load before deps
      this.worker = new Worker(new URL('./texture-worker-entry.ts', import.meta.url), {
        type: 'module',
      });

      this.worker.addEventListener('message', this.handleWorkerMessage.bind(this));
      this.worker.addEventListener('error', this.handleWorkerError.bind(this));

      // Wait for worker to be ready
      this.readyPromise = new Promise<void>((resolve) => {
        const handleReady = (event: MessageEvent) => {
          if (event.data.type === 'WORKER_READY') {
            this.isWorkerReady = true;
            this.worker?.removeEventListener('message', handleReady);
            resolve();
          }
        };
        this.worker?.addEventListener('message', handleReady);
      });
    }

    return this.readyPromise!.then(() => this.worker!);
  }

  private handleWorkerMessage = (event: MessageEvent<WorkerResponse>) => {
    const { id, type, payload, error } = event.data;

    // Ignore messages without an ID (e.g., WORKER_READY)
    if (!id) {
      return;
    }

    const pendingRequest = this.pendingRequests.get(id);

    if (!pendingRequest) {
      console.warn(`Received response for unknown request ID: ${id}`);
      return;
    }

    this.pendingRequests.delete(id);

    if (type === 'LOAD_VARIABLE_SUCCESS' && payload) {
      pendingRequest.resolve(payload);
    } else if (type === 'LOAD_VARIABLE_ERROR') {
      console.error('Request failed:', id, error);
      pendingRequest.reject(new Error(error || 'Unknown worker error'));
    }
  };

  private handleWorkerError = (error: ErrorEvent) => {
    console.error('Worker error:', error);
    // Reject all pending requests
    for (const [, pendingRequest] of this.pendingRequests) {
      pendingRequest.reject(new Error(`Worker error: ${error.message}`));
    }
    this.pendingRequests.clear();
  };

  private generateRequestId(): string {
    return `req_${++this.requestIdCounter}_${Date.now()}`;
  }

  /**
   * Batch prefetch presigned URLs for all tiles in a forecast
   */
  public async prefetchPresignedUrls(
    dataSource: DataSource,
    leadTimes: number[],
    variables: string[],
  ): Promise<void> {
    const apiBaseUrl = import.meta.env.VITE_API_BASE_URL;

    // Get auth token
    const { getAccessToken } = await import('../supabase_client');
    const authToken = await getAccessToken();

    // Build request payload
    const requests = [];
    for (const leadTime of leadTimes) {
      for (const variable of variables) {
        requests.push({ lead_time: leadTime, variable });
      }
    }

    console.log(
      `Prefetching ${requests.length} tiles for ${dataSource.model}/${dataSource.forecast}`,
    );

    try {
      const response = await fetch(`${apiBaseUrl}/v1/mapping_v2/batch-presigned-urls`, {
        method: 'POST',
        headers: {
          'Content-Type': 'application/json',
          Authorization: `Bearer ${authToken}`,
        },
        body: JSON.stringify({
          model: dataSource.model,
          forecast: dataSource.forecast,
          requests,
        }),
      });

      if (!response.ok) {
        throw new Error(`Batch prefetch failed: ${response.status} ${response.statusText}`);
      }

      const data = await response.json();
      const urls = data.urls;

      // Cache all the URLs
      for (const [key, metadata] of Object.entries(urls)) {
        const cacheKey = `${dataSource.model}_${dataSource.forecast}_${key}`;
        this.urlCache.set(cacheKey, metadata as CachedTileMetadata);
      }

      console.log(`Prefetched and cached ${Object.keys(urls).length} tiles`);
    } catch (error) {
      console.error('Batch prefetch error:', error);
      // Don't throw - fall back to individual fetches
    }
  }

  /**
   * Load texture data for a single variable using the web worker
   */
  public async loadVariableData(
    dataSource: DataSource,
    leadTime: number,
    variable: string,
  ): Promise<VariableDataFrame> {
    const baseURL = `${import.meta.env.VITE_API_BASE_URL}/v1/mapping_v2`;

    const worker = await this.initializeWorker();

    if (!this.isWorkerReady) {
      throw new Error('Worker is not ready yet');
    }

    // Check if we have a cached presigned URL
    const cacheKey = `${dataSource.model}_${dataSource.forecast}_${leadTime}_${variable}`;
    const cachedMetadata = this.urlCache.get(cacheKey);

    // Get auth token
    const { getAccessToken } = await import('../supabase_client');
    const authToken = await getAccessToken();

    const requestId = this.generateRequestId();

    return new Promise<VariableDataFrame>((resolve, reject) => {
      // Store the promise resolvers
      this.pendingRequests.set(requestId, { resolve, reject });

      // Send the request to the worker
      const message: LoadVariableRequest = {
        id: requestId,
        type: 'LOAD_VARIABLE',
        payload: {
          baseURL,
          model: dataSource.model,
          forecast: dataSource.forecast,
          leadTime,
          variable,
          authToken: authToken || undefined,
          // Include cached presigned URL if available
          cachedUrl: cachedMetadata?.url,
          cachedScale: cachedMetadata?.scale,
          cachedBounds: cachedMetadata?.bounds,
        },
      };

      worker.postMessage(message);

      // Set a timeout to prevent hanging requests
      setTimeout(() => {
        if (this.pendingRequests.has(requestId)) {
          console.warn('Request timeout for variable:', variable, requestId);
          this.pendingRequests.delete(requestId);
          reject(new Error(`Request timeout for variable: ${variable}`));
        }
      }, 60000); // 60 second timeout
    });
  }

  /**
   * Load texture data for multiple variables concurrently using the web worker
   * @param leadTime - prediction_timedelta in minutes (e.g., 60, 360, 720, 1440...)
   */
  public async loadFrameData(
    dataSource: DataSource,
    leadTime: number,
    variables: string[],
  ): Promise<Record<string, VariableDataFrame>> {
    const frame: Record<string, VariableDataFrame> = {};

    // Ensure worker is ready before proceeding
    await this.initializeWorker();

    // Load all variables in parallel using the worker
    const variablePromises = variables.map((variable) =>
      this.loadVariableData(dataSource, leadTime, variable).then((data) => ({ variable, data })),
    );

    // Wait for all variables to load
    const results = await Promise.all(variablePromises);

    // Populate the frame with the results
    for (const { variable, data } of results) {
      frame[variable] = data;
    }

    return frame;
  }

  /**
   * Terminate the worker and clean up resources
   */
  public terminate(): void {
    if (this.worker) {
      this.worker.terminate();
      this.worker = null;
    }

    // Reject all pending requests
    for (const [, pendingRequest] of this.pendingRequests) {
      pendingRequest.reject(new Error('Worker terminated'));
    }
    this.pendingRequests.clear();
  }

  /**
   * Get the number of pending requests
   */
  public getPendingRequestsCount(): number {
    return this.pendingRequests.size;
  }
}

// Create a singleton instance
export const textureLoadingService = new TextureLoadingService();
